// src/views/LoginView.js (or src/components/LoginView.js)
import React, { useEffect, useState } from 'react';
import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  confirmSignIn
} from 'aws-amplify/auth';
import { post } from 'aws-amplify/api';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '../components/support';
import './LoginView.css';
import logo from '../assets/Logo.svg';

export default function LoginView({ onLogin }) {
  // Modes: signIn | signUp | confirm | forgot | reset | requestAccount | newPassword
  const [mode, setMode] = useState('signIn');

  // Shared
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Sign-up fields (kept for future use)
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(''); // E.164: +15551234567

  // Confirmation code (sign-up + reset)
  const [code, setCode] = useState('');

  // Reset password new pass
  const [newPassword, setNewPassword] = useState('');

  // New password required (admin-created users w/ temp password)
  const [newPermanentPassword, setNewPermanentPassword] = useState('');

  // Request Account: multiple rows (now includes suffix)
  const [accountRequests, setAccountRequests] = useState([
    { firstName: '', lastName: '', suffix: '', email: '', organization: '' },
  ]);

  // UX
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    const url = new URL(window.location.href);
    const usernameFromQuery = url.searchParams.get('username');
    if (usernameFromQuery && !username) {
      setUsername(usernameFromQuery);
    }
    if (url.searchParams.get('verified') === '1') {
      setInfo('Your email was verified. Please sign in to continue.');
    }
  }, [username]);

  const resetMessages = () => {
    setError('');
    setInfo('');
  };

  // --- Sign In ---
  const handleLogin = async (e) => {
    e.preventDefault();
    resetMessages();
    try {
      const res = await signIn({ username: username.trim(), password });

      // Admin-created user with temp password -> prompt for new permanent password
      if (
        res?.nextStep?.signInStep ===
        'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        setInfo('First login requires setting a new password.');
        setNewPermanentPassword('');
        setMode('newPassword');
        return;
      }

      onLogin?.(res);
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message || 'Invalid username or password');
    }
  };

  // --- Set New Password (first-time login after temp password) ---
  const handleNewPassword = async (e) => {
    e.preventDefault();
    resetMessages();
    try {
      const res = await confirmSignIn({
        challengeResponse: newPermanentPassword,
      });
      onLogin?.(res);
    } catch (err) {
      console.error('Set new password failed:', err);
      setError(err.message || 'Could not set new password');
    }
  };

  // --- Sign Up (kept but not exposed in UI anymore) ---
  const handleSignUp = async (e) => {
    e.preventDefault();
    resetMessages();
    try {
      await signUp({
        username,
        password,
        options: {
          userAttributes: {
            name,
            email,
            phone_number: phoneNumber,
          },
        },
      });
      setInfo('We sent a confirmation code to your email/phone.');
      setMode('confirm');
    } catch (err) {
      console.error('Sign up failed:', err);
      setError(err.message || 'Sign up failed');
    }
  };

  // --- Confirm Sign Up ---
  const handleConfirm = async (e) => {
    e.preventDefault();
    resetMessages();
    try {
      await confirmSignUp({ username, confirmationCode: code });
      const user = await signIn({ username, password });
      onLogin?.(user);
    } catch (err) {
      console.error('Confirmation failed:', err);
      setError(err.message || 'Confirmation failed');
    }
  };

  const handleResend = async () => {
    resetMessages();
    try {
      await resendSignUpCode({ username });
      setInfo('A new confirmation code has been sent.');
    } catch (err) {
      console.error('Resend code failed:', err);
      setError(err.message || 'Could not resend code');
    }
  };

  // --- Forgot Password: request code ---
  const handleForgot = async (e) => {
    e.preventDefault();
    resetMessages();
    try {
      await resetPassword({ username });
      setInfo('We sent a reset code to your email/phone.');
      setMode('reset');
    } catch (err) {
      console.error('Reset password start failed:', err);
      setError(err.message || 'Could not start password reset');
    }
  };

  // --- Reset Password: confirm with code + new password ---
  const handleReset = async (e) => {
    e.preventDefault();
    resetMessages();
    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword,
      });
      setInfo('Password updated. You can sign in now.');
      setPassword('');
      setMode('signIn');
    } catch (err) {
      console.error('Confirm reset failed:', err);
      setError(err.message || 'Could not reset password');
    }
  };

  // --- Request Account: send to accountRequestsAPI REST endpoint ---
  const handleRequestAccount = async (e) => {
    e.preventDefault();
    resetMessages();

    // suffix is optional; others required
    const hasInvalidRow = accountRequests.some(
      (r) =>
        !r.firstName.trim() ||
        !r.lastName.trim() ||
        !r.email.trim() ||
        !r.organization.trim()
    );
    if (hasInvalidRow) {
      setError('Please fill out all required fields for each request.');
      return;
    }

    try {
      const restOperation = post({
        apiName: 'accountRequestsAPI',
        path: '/account-requests',
        options: {
          body: { requests: accountRequests },
        },
      });

      const { response } = await restOperation;
      // Optional: read response body if needed
      // const data = await response.body.json();

      setInfo(
        'Thank you! Your request has been submitted. Our team will contact you by email.'
      );
      setAccountRequests([
        { firstName: '', lastName: '', suffix: '', email: '', organization: '' },
      ]);
      setMode('signIn');
    } catch (err) {
      console.error('Account request failed:', err);
      setError(err.message || 'Could not submit account request');
    }
  };

  const onSubmit = (e) => {
    if (mode === 'signIn') return handleLogin(e);
    if (mode === 'newPassword') return handleNewPassword(e);
    if (mode === 'signUp') return handleSignUp(e); // kept for future
    if (mode === 'confirm') return handleConfirm(e);
    if (mode === 'forgot') return handleForgot(e);
    if (mode === 'reset') return handleReset(e);
    if (mode === 'requestAccount') return handleRequestAccount(e);
  };

  // Helpers for accountRequests array
  const updateRequestField = (index, field, value) => {
    setAccountRequests((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const addRequestRow = () => {
    setAccountRequests((prev) => [
      ...prev,
      { firstName: '', lastName: '', suffix: '', email: '', organization: '' },
    ]);
  };

  const removeRequestRow = (index) => {
    setAccountRequests((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );
  };

  const isRequestAccountMode = mode === 'requestAccount';

  return (
    <div className="login-container">
      <form onSubmit={onSubmit} className="login-form">
        <img src={logo} alt="App Logo" className="login-logo" />
        <h2 className="login-title">
          {mode === 'signIn' && 'Sign In'}
          {mode === 'signUp' && 'Create Account'}
          {mode === 'confirm' && 'Confirm Your Account'}
          {mode === 'forgot' && 'Forgot Password'}
          {mode === 'reset' && 'Reset Password'}
          {mode === 'requestAccount' && 'Request an Account'}
          {mode === 'newPassword' && 'Set New Password'}
        </h2>

        {/* Username only for auth-related modes */}
        {!isRequestAccountMode && (
          <input
            type="text"
            placeholder="Username"
            className="login-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        )}

        {/* Password field for sign-in/sign-up (not request/confirm/forgot/reset/newPassword) */}
        {(mode === 'signIn' || mode === 'signUp') && !isRequestAccountMode && (
          <input
            type="password"
            placeholder="Password"
            className="login-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        )}

        {/* New password required field */}
        {mode === 'newPassword' && (
          <input
            type="password"
            placeholder="New Password"
            className="login-input"
            value={newPermanentPassword}
            onChange={(e) => setNewPermanentPassword(e.target.value)}
            required
          />
        )}

        {/* Sign-up only fields (kept for future use) */}
        {mode === 'signUp' && (
          <>
            <input
              type="text"
              placeholder="Full Name"
              className="login-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              type="email"
              placeholder="Email"
              className="login-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              type="tel"
              placeholder="Phone (+15551234567)"
              className="login-input"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
            />
          </>
        )}

        {/* Request Account fields */}
        {isRequestAccountMode && (
          <div className="request-account-section">
            {accountRequests.map((row, index) => (
              <div key={index} className="request-row">
                <input
                  type="text"
                  placeholder="First Name"
                  className="login-input"
                  value={row.firstName}
                  onChange={(e) =>
                    updateRequestField(index, 'firstName', e.target.value)
                  }
                  required
                />

                <input
                  type="text"
                  placeholder="Last Name"
                  className="login-input"
                  value={row.lastName}
                  onChange={(e) =>
                    updateRequestField(index, 'lastName', e.target.value)
                  }
                  required
                />

                <input
                  type="text"
                  placeholder="Suffix (MD, DO, NP, PA, etc.)"
                  className="login-input"
                  value={row.suffix || ''}
                  onChange={(e) =>
                    updateRequestField(index, 'suffix', e.target.value)
                  }
                />

                <input
                  type="email"
                  placeholder="Email"
                  className="login-input"
                  value={row.email}
                  onChange={(e) =>
                    updateRequestField(index, 'email', e.target.value)
                  }
                  required
                />

                <input
                  type="text"
                  placeholder="Organization"
                  className="login-input"
                  value={row.organization}
                  onChange={(e) =>
                    updateRequestField(index, 'organization', e.target.value)
                  }
                  required
                />

                {accountRequests.length > 1 && (
                  <button
                    type="button"
                    className="small-link-button"
                    onClick={() => removeRequestRow(index)}
                    style={{ alignSelf: 'flex-end' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              className="link-button link-button-large"
              onClick={addRequestRow}
              style={{ marginTop: '8px' }}
            >
              + Add another person
            </button>
          </div>
        )}

        {/* Confirm sign-up & reset password code input */}
        {(mode === 'confirm' || mode === 'reset') && (
          <input
            type="text"
            placeholder="Confirmation Code"
            className="login-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        )}

        {/* New password (reset only) */}
        {mode === 'reset' && (
          <input
            type="password"
            placeholder="New Password"
            className="login-input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        )}

        {/* Error/info messaging */}
        {error && (
          <div className="login-error">
            {error}{' '}
            <span>
              If this keeps happening, email{' '}
              <a href={SUPPORT_MAILTO} rel="noopener noreferrer">
                {SUPPORT_EMAIL}
              </a>.
            </span>
          </div>
        )}
        {info && <div className="login-info">{info}</div>}

        <button type="submit" className="login-button">
          {mode === 'signIn' && 'Login'}
          {mode === 'newPassword' && 'Update Password'}
          {mode === 'signUp' && 'Create Account'}
          {mode === 'confirm' && 'Confirm'}
          {mode === 'forgot' && 'Send Code'}
          {mode === 'reset' && 'Update Password'}
          {mode === 'requestAccount' && 'Submit Request'}
        </button>

        {/* Mode switches */}
        {mode === 'signIn' && (
          <div className="login-switch">
            <button
              type="button"
              className="link-button link-button-large link-button-cta"
              onClick={() => {
                resetMessages();
                setMode('requestAccount');
              }}
            >
              Request an account
            </button>

            <button
              type="button"
              className="link-button link-button-large link-button-cta"
              onClick={() => {
                resetMessages();
                setMode('forgot');
              }}
            >
              Forgot password?
            </button>
          </div>
        )}

        {mode === 'requestAccount' && (
          <div className="login-switch">
            <button
              type="button"
              className="link-button"
              onClick={() => {
                resetMessages();
                setMode('signIn');
              }}
            >
              Back to Sign in
            </button>
          </div>
        )}

        {mode === 'signUp' && (
          <div className="login-switch">
            <span>Already have an account? </span>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                resetMessages();
                setMode('signIn');
              }}
            >
              Sign in
            </button>
          </div>
        )}

        {mode === 'confirm' && (
          <div className="login-switch">
            <button type="button" className="link-button" onClick={handleResend}>
              Resend code
            </button>
            <span style={{ margin: '0 8px' }}>•</span>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                resetMessages();
                setMode('signIn');
              }}
            >
              Back to Sign in
            </button>
          </div>
        )}

        {mode === 'forgot' && (
          <div className="login-switch">
            <button
              type="button"
              className="link-button"
              onClick={() => {
                resetMessages();
                setMode('signIn');
              }}
            >
              Back to Sign in
            </button>
          </div>
        )}

        {mode === 'reset' && (
          <div className="login-switch">
            <button
              type="button"
              className="link-button"
              onClick={() => {
                resetMessages();
                setMode('signIn');
              }}
            >
              Back to Sign in
            </button>
          </div>
        )}

        {mode === 'newPassword' && (
          <div className="login-switch">
            <button
              type="button"
              className="link-button"
              onClick={() => {
                resetMessages();
                setNewPermanentPassword('');
                setMode('signIn');
              }}
            >
              Back to Sign in
            </button>
          </div>
        )}

        {/* Persistent support link */}
        <div className="login-support">
          <span>Need help? </span>
          <a
            href={SUPPORT_MAILTO}
            className="link-button"
            rel="noopener noreferrer"
          >
            Contact support
          </a>
        </div>
      </form>
    </div>
  );
}
